<template>
  <div>
    <filter-manager
      ref="filterActionPlan"
      v-model="filter.model"
      display-advanced
      items-container-class="flex-wrap"
      actions-container-class="col-sm-3 ml-0 mr-0 text-right"
      :filters="filtersHeader"
      @search="() => onSearch()"
    />
    <custom-separator class="my-4" text="Resultados" />

    <div v-if="results.length > 0">
      <div class="mb-3" v-for="(item, index) in results">
        <plan-action-result
          :item="item"
          :index="index"
          :academic-year-id="filter.model.academicYear?.id"
          :priority-levels="filter.options.priorityLevels"
          @refresh="onSearch"
          @toggle="toggle"
        />
      </div>

      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onPageChanged"
      />
    </div>
    <div v-else>
      <custom-result-not-found />
    </div>
  </div>
</template>

<script>
import alert from '@/mixin/alert.mixin';
import storeMixin from '@/mixin/storeGenerator';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import PlanActionResult from './components/PlanActionResult.vue';
import CustomResultNotFound from 'custom-result-not-found';
import * as api from '@/views/pei/location-alternatives/_api';
import {
  getPriorityLevelTypes,
  getActionPlanStatus,
  search,
} from '@/services/operations/action-plan';

export default {
  name: 'action-plan',
  mixins: [alert, storeMixin],
  components: {
    Pagination,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    PlanActionResult,
  },
  data() {
    return {
      shouldValidateCenter: false,
      isInitialized: false,
      filter: {
        model: {
          actionPlanNumber: null,
          status: null,
          academicYear: null,
          priorityLevel: null,
          ore: null,
          center: null,
          municipality: null,
          school: null,
          alternativeCategory: null,
          alternative: null,
        },
        options: {
          statuses: [],
          academicYears: [],
          priorityLevels: [],
          ores: [],
          centers: [],
          municipalities: [],
          schools: [],
          alternativeCategories: [],
          alternatives: [],
        },
      },
      results: [],
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: 10,
      },
    };
  },
  async mounted() {
    await this.init();
    this.isInitialized = true;
  },

  methods: {
    async init() {
      await this.getAcademicYears();
      await Promise.all([
        this.getPriorityLevelTypes(),
        this.getActionPlanStatus(),
        this.getAlternativeCategories(),
        this.getCatalogOres(),
      ]);
    },
    clearModels(fields = []) {
      fields.forEach((field) => {
        if (this.filter.model.hasOwnProperty(field)) {
          this.filter.model[field] = null;
        }
      });
    },
    onPageChanged(page) {
      this.onSearch(page);
    },
    async getAcademicYears() {
      const academicYears = await api.getAcademicYears();
      this.filter.options.academicYears = academicYears;
      this.filter.model.academicYear =
        this.filter.model.academicYear ??
        this.filter.options.academicYears.find((x) => x.selected);
      await this.getCatalogCenters();
    },
    async onSearch(page = 1) {
      this.shouldValidateCenter = false;

      if (this.filter.model.center && this.filter.model.ore) {
        this.ShowToast(
          '',
          'Debe seleccionar ORE o Centro, pero no ambos. Ajuste su selección para continuar con la búsqueda.',
          'error'
        );
        return;
      }

      const payload = {
        actionPlanNumber: this.filter.model.actionPlanNumber,
        academicYearId: this.filter.model.academicYear?.id,
        centerId: this.filter.model.center?.id,
        oreId: this.filter.model.ore?.id,
        statusId: this.filter.model.status?.id,
        priorityLevelId: this.filter.model.priorityLevel?.id,
        municipalityId: this.filter.model.municipality?.id,
        schoolId: this.filter.model.school?.id,
        alternativeCategoryId: this.filter.model.alternativeCategory?.id,
        alternativeNameId: this.filter.model.alternative?.id,
        currentPage: page,
        pageSize: this.pagination.pageSize,
      };
      const { data } = await search(payload);
      this.results = data.data.map((x) => ({ ...x, isOpen: false }));
      this.pagination = {
        ...this.pagination,
        count: data.count,
        currentPage: data.currentPage,
        pageCount: data.pageCount,
        totalItemCount: data.totalItemCount,
      };
    },
    async setFilterAcademicYear(academicYear) {
      this.filter.academicYear = academicYear;
    },
    async getPriorityLevelTypes() {
      const { data } = await getPriorityLevelTypes();
      this.filter.options.priorityLevels = data;
    },
    async getActionPlanStatus() {
      const { data } = await getActionPlanStatus();
      this.filter.options.statuses = data;
    },
    async getCatalogCenters() {
      const data = await await api.getCenters({
        academicYearId: this.filter.model.academicYear?.id,
      });
      this.filter.options.centers = data;

      if (this.filter.options.centers.length == 1) {
        this.filter.model.center =
          this.filter.model.center ?? this.filter.options.centers[0];

        this.getCatalogMunicipalities();
      }
    },
    async getCatalogOres() {
      const data = await await api.getOres();
      this.filter.options.ores = data;
    },
    async getCatalogMunicipalities() {
      const data = await api.getMunicipalities({
        academicYearId: this.filter.model.academicYear?.id,
        centerId: this.filter.model.center?.id,
        regionId: this.filter.model.ore?.id, // ore is region really
      });
      this.filter.options.municipalities = data;

      if (this.filter.options.municipalities.length == 1) {
        this.filter.model.municipality =
          this.filter.model.municipality ??
          this.filter.options.municipalities[0];
        this.getCatalogSchools();
      }
    },
    async getCatalogSchools() {
      const data = await api.getSchools({
        academicYearId: this.filter.model.academicYear?.id,
        centerId: this.filter.model.center?.id,
        municipalityId: this.filter.model.municipality?.id,
      });

      this.filter.options.schools = data;

      if (this.filter.options.schools.length == 1) {
        this.filter.model.school =
          this.filter.model.school ?? this.filter.options.schools[0];
      }
    },
    async getAlternativeCategories() {
      const data = await api.getCategories();
      this.filter.options.alternativeCategories = data;

      if (this.filter.options.alternativeCategories.length == 1) {
        this.filter.model.alternativeCategory = this.filter.options.alternativeCategories[0];
      }
    },
    async getAlternativeNames() {
      if (!this.filter.model.alternativeCategory?.id) return;

      const data = await api.getNames({
        categoryId: this.filter.model.alternativeCategory?.id,
        academicYearId: this.filter.model.academicYear?.id,
      });

      this.filter.options.alternatives = data;
      if (this.filter.options.alternatives.length == 1) {
        this.filter.model.alternative =
          this.filter.model.alternative ?? this.filter.options.alternatives[0];
      }
    },
    toggle({ id }) {
      this.results
        .filter((x) => x.id !== id)
        .forEach((x) => (x.isOpen = false));
    },
  },
  computed: {
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-12 col-md-3',
          classContainer: 'w-100',
          validations: 'required',
          onChanged: () => {
            if (this.isInitialized) {
              this.clearModels(['center', 'municipality', 'school']);
            }
            this.getCatalogCenters();
          },
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-12 col-md-3',
          classContainer: 'w-100',
        },
        {
          name: 'priorityLevel',
          component: 'CustomDropDown',
          options: this.filter.options.priorityLevels,
          value: this.filter.model.priorityLevel,
          placeholder: 'Nivel de Prioridad',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-12 col-md-3',
          classContainer: 'w-100',
          clearable: true,
        },
        {
          name: 'actionPlanNumber',
          component: 'CustomInput',
          value: this.filter.model.actionPlanNumber,
          placeholder: 'Buscar por número de plan',
          fieldName: 'name',
          fieldKey: 'id',
          icon: 'fa fa-search color-orange',
          leftIcon: true,
          clearable: false,
          class: 'col-sm-12 col-md-3',
          classContainer: 'w-100',
          clearable: true,
          type: 'text',
        },
        {
          name: 'ore',
          component: 'CustomDropDown',
          options: this.filter.options.ores,
          value: this.filter.model.ore,
          label: 'ORE:',
          placeholder: 'Seleccione',
          class: 'col-12 col-md-4',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          clearable: true,
          validations:
            this.shouldValidateCenter && !this.filter.model.center
              ? 'required'
              : '',
          error: 'La ORE es requerida.',
          onChanged: () => {
            if (this.isInitialized) {
              this.clearModels(['municipality', 'school']);
            }
            this.getCatalogMunicipalities();
          },
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.filter.options.centers,
          value: this.filter.model.center,
          label: 'Centro:',
          placeholder: 'Seleccione',
          class: 'col-12 col-md-4',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          clearable: true,
          validations:
            this.shouldValidateCenter && !this.filter.model.ore
              ? 'required'
              : '',
          error: 'El centro es requerido.',
          disabled: this.filter.options.centers.length == 1,
          onChanged: () => {
            if (this.isInitialized) {
              this.clearModels(['municipality', 'school']);
            }
            this.getCatalogMunicipalities();
          },
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filter.options.municipalities,
          value: this.filter.model.municipality,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Municipio:',
          placeholder: 'Seleccione',
          clearable: true,
          isAdvanced: true,
          disabled: this.filter.options.municipalities.length == 1,
          onChanged: () => {
            if (this.isInitialized) {
              this.clearModels(['school']);
            }
            this.getCatalogSchools();
          },
          disabled: this.filter.model.center === null,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.filter.options.schools,
          value: this.filter.model.school,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Escuela:',
          placeholder: 'Seleccione',
          clearable: true,
          disabled:
            this.filter.model.municipality === null ||
            this.filter.options.schools.length == 1,
          isAdvanced: true,
        },
        {
          name: 'alternativeCategory',
          component: 'CustomDropDown',
          options: this.filter.options.alternativeCategories,
          value: this.filter.model.alternativeCategory,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Categoría de la Alternativa:',
          placeholder: 'Seleccione',
          onChanged: () => {
            if (this.isInitialized) {
              this.clearModels(['alternative']);
            }
            this.getAlternativeNames();
          },
          disabled: this.filter.options.alternativeCategories.length == 1,
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'alternative',
          label: 'Nombre de la Alternativa:',
          component: 'CustomDropDown',
          options: this.filter.options.alternatives,
          value: this.filter.model.alternative,
          placeholder: 'Seleccione',
          class: 'col-12 col-md-4',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filter.model.alternative == null,
          isAdvanced: true,
          clearable: true,
          disabled: this.filter.options.alternatives.length == 1,
        },
      ];
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const ROUTE_NAME_FROM = 'location-alternatives-action-plan';

    next((vm) => {
      if (from.name == ROUTE_NAME_FROM) {
        vm.recoveryData();
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
};
</script>
